// dpc-puntos-ui.js
// Pantalla cinemática de PUNTOS GANADOS al completar un pedido.
//
// Se monta como overlay full-screen cuando:
//   1. Push notif "puntos_otorgados" → URL hash #puntos-<orderId>
//   2. window.dpcMostrarPuntos(orderId) llamado manualmente
//
// Trae el resumen de puntos del backend (saldo, tier, progreso) y muestra:
//   - Confetti pasivo de fondo
//   - Glows naranja/dorado animados
//   - Trofeo 🏆 con bounce entry
//   - Count-up animado del número de puntos ganados
//   - Card del tier actual (color según Bronze/Silver/Gold)
//   - Progress bar al próximo tier
//   - Saldo total actualizado
//   - Sonidos: success + cheers
//   - CTA "Continuar" que cierra con burst
//
// El estado "ya mostrado" se persiste por orderId en localStorage para no
// volver a mostrar la misma celebración si el cliente reabre la app.

(function () {
  'use strict';

  const Z = 2147483647;
  const PUSH_API_BASE = window.DPC_PUSH_API_BASE || 'https://drinking-partners-calendar-app.fly.dev';

  function alreadyShown(orderId) {
    try { return localStorage.getItem('dpc_puntos_shown_' + orderId) === '1'; } catch (e) { return false; }
  }
  function markShown(orderId) {
    try { localStorage.setItem('dpc_puntos_shown_' + orderId, '1'); } catch (e) {}
  }

  function getEmail() {
    try { return (window.user && window.user.email) || ''; } catch (e) { return ''; }
  }

  async function fetchPuntos() {
    const email = getEmail();
    if (!email) return null;
    try {
      const r = await fetch(PUSH_API_BASE + '/api/puntos?email=' + encodeURIComponent(email));
      if (!r.ok) return null;
      return await r.json();
    } catch (e) {
      console.warn('[dpc-puntos] fetch falló:', e);
      return null;
    }
  }

  // ===== Helpers visuales =====
  function injectStyles() {
    if (document.getElementById('dpc-puntos-css')) return;
    const st = document.createElement('style');
    st.id = 'dpc-puntos-css';
    st.textContent = `
      @keyframes dpcPtConfFall{0%{transform:translateY(-30px) rotate(var(--rotStart));opacity:0}15%{opacity:1}90%{opacity:1}100%{transform:translateY(110vh) rotate(var(--rotEnd));opacity:0}}
      @keyframes dpcPtGlowA{0%,100%{opacity:0.28;transform:translate(-50%,-50%) scale(1)}50%{opacity:0.52;transform:translate(-50%,-50%) scale(1.18)}}
      @keyframes dpcPtGlowB{0%,100%{opacity:0.16;transform:translate(-50%,-50%) scale(1.12)}50%{opacity:0.32;transform:translate(-50%,-50%) scale(0.96)}}
      @keyframes dpcPtTrophyPop{0%{transform:scale(0) rotate(-180deg);opacity:0}45%{transform:scale(1.35) rotate(12deg);opacity:1}65%{transform:scale(0.94) rotate(-6deg)}85%{transform:scale(1.06) rotate(3deg)}100%{transform:scale(1) rotate(0deg);opacity:1}}
      @keyframes dpcPtTrophyFloat{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-8px) rotate(-3deg)}}
      @keyframes dpcPtSlideUp{from{transform:translateY(32px);opacity:0;filter:blur(4px)}to{transform:translateY(0);opacity:1;filter:blur(0)}}
      @keyframes dpcPtCountPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
      @keyframes dpcPtShimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
      @keyframes dpcPtBtnGlow{0%,100%{box-shadow:0 6px 22px rgba(255,145,77,0.42),0 0 0 0 rgba(255,145,77,0.55)}50%{box-shadow:0 8px 28px rgba(255,145,77,0.62),0 0 0 12px rgba(255,145,77,0)}}
      @keyframes dpcPtTierBadge{0%,100%{box-shadow:0 0 0 0 rgba(255,145,77,0.6)}50%{box-shadow:0 0 0 14px rgba(255,145,77,0)}}
      @keyframes dpcPtProgress{from{width:0}to{width:var(--target-w)}}
      .dpc-pt-glow{position:absolute;border-radius:50%;filter:blur(60px);pointer-events:none;will-change:transform,opacity}
      .dpc-pt-conf{position:absolute;animation-name:dpcPtConfFall;animation-timing-function:linear;animation-iteration-count:infinite;animation-fill-mode:forwards;pointer-events:none;will-change:transform,opacity}
    `;
    document.head.appendChild(st);
  }

  function confettiHTML(count) {
    const colors = ['#ff914d','#ffcb6e','#fff','#8be08b','#ff7733','#ffd88c','#a78bfa'];
    const out = [];
    for (let i = 0; i < count; i++) {
      const c = colors[i % colors.length];
      const x = 3 + (i * 7.3) % 94;
      const delay = (i * 0.21) % 3;
      const dur = 3.8 + (i % 4) * 0.6;
      const w = i % 3 === 0 ? 8 : (i % 2 === 0 ? 10 : 7);
      const h = w + 4;
      const rotStart = (i * 53) % 360;
      const rotEnd = rotStart + 540 + (i * 40);
      const rad = i % 4 === 0 ? '50%' : '2px';
      out.push(`<div class="dpc-pt-conf" style="left:${x}vw;top:-22px;width:${w}px;height:${h}px;background:${c};border-radius:${rad};animation-delay:${delay}s;animation-duration:${dur}s;--rotStart:${rotStart}deg;--rotEnd:${rotEnd}deg"></div>`);
    }
    return out.join('');
  }

  function glowsHTML() {
    return `
      <div class="dpc-pt-glow" style="top:-90px;right:-70px;width:320px;height:320px;background:radial-gradient(circle,rgba(255,145,77,0.5),transparent 70%);animation:dpcPtGlowA 5.5s ease-in-out infinite"></div>
      <div class="dpc-pt-glow" style="top:50%;left:50%;width:440px;height:440px;background:radial-gradient(circle,rgba(255,145,77,0.2),transparent 65%);transform:translate(-50%,-50%);animation:dpcPtGlowA 7s ease-in-out 0.4s infinite"></div>
      <div class="dpc-pt-glow" style="bottom:-110px;left:-90px;width:380px;height:380px;background:radial-gradient(circle,rgba(255,203,110,0.3),transparent 70%);animation:dpcPtGlowB 6.5s ease-in-out 0.8s infinite"></div>
    `;
  }

  // ===== Sonidos (reusan los de dpc-install-gate si están) =====
  function playSound(name) {
    try { if (typeof window.dpcSound === 'function') window.dpcSound(name); } catch (e) {}
  }

  // ===== Render principal =====
  function buildOverlay(data, orderId, ganadosOverride) {
    injectStyles();
    const tier = data.tier || data.tier_actual || 'Bronze';
    const tierEmoji = tier === 'Gold' || tier === 'Oro' ? '🥇' : tier === 'Silver' || tier === 'Plata' ? '🥈' : '🥉';
    const tierColor = tier === 'Gold' || tier === 'Oro' ? '#ffd700' : tier === 'Silver' || tier === 'Plata' ? '#c0c0c0' : '#cd7f32';
    const total = +(data.total || data.puntosTotal || 0);
    const lifetime = +(data.lifetime || data.puntosLifetime || total);
    const faltan = +(data.faltanParaProximoTier || (data.proximoTier && data.proximoTier.faltan) || 0);
    const proxNombre = data.proximoTier && (data.proximoTier.nombre || data.proximoTier) || null;
    // Calcular % progreso al próximo tier (visual)
    let pctProgreso = 100;
    if (faltan > 0 && proxNombre) {
      // Si conocemos los umbrales podemos calcular bien. Si no, estimamos.
      const totalAlSiguiente = lifetime + faltan;
      pctProgreso = totalAlSiguiente > 0 ? Math.min(100, Math.round((lifetime / totalAlSiguiente) * 100)) : 100;
    }
    // Puntos ganados en este pedido — preferimos el override (del push payload) si vino
    const ganados = +ganadosOverride || 0;

    const el = document.createElement('div');
    el.id = 'dpc-puntos-overlay';
    el.style.cssText = `position:fixed;inset:0;background:linear-gradient(180deg,#1f1410 0%,#160d0a 35%,#100a08 70%,#0a0608 100%);background-color:#0a0608;z-index:${Z};overflow-y:auto;overflow-x:hidden;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#fff;-webkit-overflow-scrolling:touch;min-height:100vh;min-height:100dvh`;
    el.innerHTML = `
      <div style="position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:0">
        ${glowsHTML()}
        ${confettiHTML(14)}
      </div>
      <div style="padding:calc(env(safe-area-inset-top,20px) + 70px) 22px calc(env(safe-area-inset-bottom,20px) + 40px);text-align:center;max-width:480px;margin:0 auto;width:100%;box-sizing:border-box;position:relative;z-index:2;min-height:100vh;min-height:100dvh">

        <div style="font-size:80px;line-height:1;margin-bottom:10px;filter:drop-shadow(0 8px 28px rgba(255,145,77,0.6));display:inline-block;animation:dpcPtTrophyPop 0.85s cubic-bezier(0.34,1.56,0.64,1) forwards, dpcPtTrophyFloat 3.2s ease-in-out 1s infinite">🏆</div>

        <div style="font-size:13px;font-weight:900;color:#ff914d;letter-spacing:1.8px;text-transform:uppercase;margin-bottom:10px;opacity:0;animation:dpcPtSlideUp 0.6s cubic-bezier(0.16,1,0.3,1) 0.45s forwards">¡Pedido completado!</div>

        <div style="font-size:30px;font-weight:900;letter-spacing:-0.8px;margin-bottom:6px;line-height:1.1;color:#fff;text-shadow:0 2px 18px rgba(255,145,77,0.4);opacity:0;animation:dpcPtSlideUp 0.65s cubic-bezier(0.16,1,0.3,1) 0.55s forwards">Ganaste puntos 🎉</div>

        <div style="font-size:14px;color:#ccc;line-height:1.5;margin-bottom:22px;opacity:0;animation:dpcPtSlideUp 0.6s cubic-bezier(0.16,1,0.3,1) 0.7s forwards">Gracias por arrendar con nosotros</div>

        <!-- HERO: número grande de puntos ganados con count-up -->
        <div style="background:linear-gradient(135deg,rgba(255,145,77,0.18),rgba(255,203,110,0.06));border:1.5px solid rgba(255,145,77,0.4);border-radius:22px;padding:24px 20px;margin-bottom:18px;box-shadow:0 12px 36px rgba(0,0,0,0.45),0 0 0 1px rgba(255,145,77,0.08) inset;opacity:0;animation:dpcPtSlideUp 0.7s cubic-bezier(0.16,1,0.3,1) 0.85s forwards">
          <div style="font-size:11px;font-weight:900;color:#ffcb6e;letter-spacing:1.4px;text-transform:uppercase;margin-bottom:8px">Puntos ganados</div>
          <div id="dpc-pt-count" style="font-size:68px;font-weight:900;line-height:1;letter-spacing:-2px;background:linear-gradient(135deg,#ff914d 0%,#ffcb6e 50%,#ff914d 100%);background-size:200% 100%;-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;animation:dpcPtShimmer 3s ease-in-out infinite,dpcPtCountPulse 1.6s ease-in-out 1.5s infinite;text-shadow:0 4px 24px rgba(255,145,77,0.3)">+0</div>
          <div style="font-size:13px;color:#aaa;margin-top:8px">pts agregados a tu saldo</div>
        </div>

        <!-- TIER ACTUAL -->
        <div style="background:linear-gradient(135deg,rgba(255,255,255,0.05),rgba(255,255,255,0.01));border:1px solid rgba(255,255,255,0.1);border-radius:18px;padding:18px;margin-bottom:14px;text-align:left;opacity:0;animation:dpcPtSlideUp 0.65s cubic-bezier(0.16,1,0.3,1) 1s forwards">
          <div style="display:flex;align-items:center;gap:14px;margin-bottom:${proxNombre ? '14px' : '0'}">
            <div style="font-size:46px;line-height:1;filter:drop-shadow(0 4px 14px ${tierColor}80);animation:dpcPtTierBadge 2.2s ease-in-out infinite;border-radius:50%;width:64px;height:64px;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,${tierColor}30,${tierColor}10);flex-shrink:0">${tierEmoji}</div>
            <div style="flex:1;min-width:0">
              <div style="font-size:11px;font-weight:800;color:#aaa;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:2px">Tu nivel</div>
              <div style="font-size:20px;font-weight:900;color:#fff;line-height:1.1;margin-bottom:2px">${tier}</div>
              <div style="font-size:12px;color:${tierColor};font-weight:700">${total.toLocaleString('es-CL')} pts acumulados</div>
            </div>
          </div>
          ${proxNombre ? `
          <div>
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
              <div style="font-size:11px;font-weight:800;color:#aaa;text-transform:uppercase;letter-spacing:0.6px">Próximo: ${proxNombre}</div>
              <div style="font-size:11px;font-weight:800;color:#ff914d">${faltan.toLocaleString('es-CL')} pts</div>
            </div>
            <div style="height:8px;background:rgba(255,255,255,0.06);border-radius:4px;overflow:hidden">
              <div style="height:100%;background:linear-gradient(90deg,#ff914d,#ffcb6e);border-radius:4px;box-shadow:0 0 12px rgba(255,145,77,0.5);--target-w:${pctProgreso}%;animation:dpcPtProgress 1.2s cubic-bezier(0.34,1.4,0.64,1) 1.3s forwards;width:0"></div>
            </div>
          </div>
          ` : `<div style="font-size:12px;color:#8be08b;text-align:center;padding:8px 0">🎉 ¡Has alcanzado el máximo nivel!</div>`}
        </div>

        <button onclick="window.dpcCerrarPuntos()" style="position:relative;overflow:hidden;display:flex;align-items:center;justify-content:center;gap:10px;width:100%;background:linear-gradient(135deg,#ff914d 0%,#e6742d 100%);border:none;color:#fff;font-size:15.5px;font-weight:800;padding:17px 20px;border-radius:16px;cursor:pointer;font-family:inherit;letter-spacing:-0.2px;opacity:0;animation:dpcPtSlideUp 0.7s cubic-bezier(0.16,1,0.3,1) 1.15s forwards, dpcPtBtnGlow 2.4s ease-in-out 2.5s infinite;margin-top:6px">
          <span>Seguir explorando</span>
          <span style="font-size:18px">→</span>
        </button>
      </div>
    `;
    return el;
  }

  // Count-up animado del número de puntos ganados
  function animateCountUp(targetEl, targetNum, duration) {
    if (!targetEl || targetNum <= 0) { if (targetEl) targetEl.textContent = '+' + targetNum; return; }
    const start = performance.now();
    function frame(now) {
      const t = Math.min(1, (now - start) / duration);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const val = Math.round(targetNum * eased);
      targetEl.textContent = '+' + val.toLocaleString('es-CL');
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  window.dpcCerrarPuntos = function () {
    const el = document.getElementById('dpc-puntos-overlay');
    if (!el) return;
    el.style.transition = 'opacity 0.45s ease-in,transform 0.45s ease-in';
    el.style.opacity = '0';
    el.style.transform = 'scale(0.98)';
    setTimeout(() => { try { el.remove(); } catch (e) {} document.body.style.overflow = ''; }, 480);
    // Limpiar el hash para que no reabra al navegar
    try {
      if (location.hash && location.hash.startsWith('#puntos-')) {
        history.replaceState(null, '', location.pathname + location.search);
      }
    } catch (e) {}
  };

  window.dpcMostrarPuntos = async function (orderId, puntosGanadosOverride) {
    if (!orderId) return;
    if (alreadyShown(orderId)) return; // ya se mostró
    if (document.getElementById('dpc-puntos-overlay')) return;
    const data = await fetchPuntos();
    if (!data) {
      console.warn('[dpc-puntos] no se pudo obtener resumen — mostramos fallback');
    }
    const el = buildOverlay(data || { total: 0, tier: 'Bronze' }, orderId, puntosGanadosOverride);
    document.body.appendChild(el);
    document.body.style.overflow = 'hidden';
    markShown(orderId);
    // Sonidos: success al abrir + cheers a los 400ms
    playSound('success');
    setTimeout(() => playSound('cheers'), 400);
    // Count-up del número de puntos ganados (empieza ~0.9s para sincronizar con la entrada)
    const ganados = puntosGanadosOverride || 0;
    if (ganados > 0) {
      const countEl = document.getElementById('dpc-pt-count');
      setTimeout(() => animateCountUp(countEl, ganados, 1100), 900);
    }
  };

  // ===== Auto-trigger por deep link =====
  // URL: /pages/app?ref=push&pts=<n>#puntos-<orderId>
  // El push payload puede incluir ?pts=<n> en la URL como hint para el count-up
  function detectFromHash() {
    const hash = location.hash || '';
    const m = hash.match(/#puntos-(.+)$/);
    if (!m) return;
    const orderId = decodeURIComponent(m[1]);
    // Hint del nro de puntos via query string
    let puntos = 0;
    try {
      const u = new URL(location.href);
      puntos = +(u.searchParams.get('pts') || 0);
    } catch (e) {}
    // Esperar a que window.user esté cargado
    let tries = 0;
    const iv = setInterval(() => {
      if (window.user && window.user.email) {
        clearInterval(iv);
        window.dpcMostrarPuntos(orderId, puntos);
      } else if (++tries > 40) {
        clearInterval(iv);
        // Sin login, mostramos algo basal igual
        window.dpcMostrarPuntos(orderId, puntos);
      }
    }, 250);
  }

  if (document.readyState === 'complete') detectFromHash();
  else window.addEventListener('load', detectFromHash);
  window.addEventListener('hashchange', detectFromHash);

  // ===== Auto-disparo cuando hay un arriendo recién finalizado =====
  // Cada vez que se cargan las orders (dpcOrdersLoaded), revisamos si hay
  // un arriendo con estado=finalizado + puntosCelebrar Y no se ha mostrado.
  // Si sí → abrir la pantalla automáticamente. Así Sebastián marca completado
  // en admin → cliente abre la PWA → celebración inmediata sin tocar nada.
  function detectArriendosFinalizados() {
    try {
      const os = window.dpcGetOrders ? window.dpcGetOrders() : window.ordersState;
      if (!os || !Array.isArray(os.data)) {
        console.log('[dpc-puntos] detect: no hay orders cargadas todavía');
        return;
      }
      let evaluados = 0, candidatos = 0;
      for (const o of os.data) {
        evaluados++;
        if (!o || !o.cierre) continue;
        if (o.cierre.estadoArriendo !== 'finalizado') continue;
        if (!o.cierre.puntosCelebrar || !o.cierre.puntosCelebrar.puntos) continue;
        candidatos++;
        const orderId = o.id || o.name;
        // Si los puntos son MUY RECIENTES (últimas 48h), ignoramos el
        // localStorage — el cliente quizás no llegó a verla por bugs viejos.
        // Si son más viejos, respetamos el localStorage.
        const fechaPuntos = o.cierre.puntosCelebrar.fecha
          ? new Date(o.cierre.puntosCelebrar.fecha).getTime()
          : 0;
        const horasDesdePuntos = (Date.now() - fechaPuntos) / 3600000;
        const recientes = horasDesdePuntos < 48;
        if (!recientes && alreadyShown(orderId)) {
          console.log('[dpc-puntos] detect: ya mostrado para', orderId);
          continue;
        }
        // Confirmar que no hay otra pantalla abierta antes de disparar
        if (document.getElementById('dpc-puntos-overlay')) return;
        console.log('[dpc-puntos] DISPARANDO pantalla:', orderId, '+', o.cierre.puntosCelebrar.puntos, 'pts (horas desde otorgados:', horasDesdePuntos.toFixed(1), ')');
        window.dpcMostrarPuntos(orderId, o.cierre.puntosCelebrar.puntos);
        return;
      }
      console.log('[dpc-puntos] detect: evaluados=' + evaluados + ', candidatos=' + candidatos);
    } catch (e) { console.warn('[dpc-puntos] detectArriendosFinalizados:', e); }
  }
  window.addEventListener('dpcOrdersLoaded', () => setTimeout(detectArriendosFinalizados, 800));
  // También polling de respaldo cada 6s por si el evento no se disparó
  setInterval(detectArriendosFinalizados, 6000);

  console.log('[dpc-puntos-ui] cargado v2 (auto-disparo arriendo finalizado) ✓');
})();
